import z from "zod/v4";

// ──────────────────────────────────────────────────────────────────────────
// Generic condition-tree engine shared by every resource that lets merchants
// build rule condition trees against a field registry (blocklist, samples).
// A field registry is the single source of truth: the schema's discriminated
// union, the signal-key lookup and the CreatorSignals shape are all derived
// from it, so adding/removing a field only ever means editing the registry.
// ──────────────────────────────────────────────────────────────────────────

export type FieldKind = "numeric" | "tag";

export interface FieldDefinition {
  kind: FieldKind;
  label: string;
  tooltip: string;
  /** Property name to read on CreatorSignals, when it differs from the field id. */
  signalKey?: string;
}

export type ComparisonOperator = "eq" | "gt" | "gte" | "lt" | "lte" | "between";
export type TagOperator = "is" | "is_not";
export type Operator = ComparisonOperator | TagOperator;
export type ConditionValue = number | string | [number, number];

export type CreatorSignalsFor<Defs extends Record<string, FieldDefinition>> = {
  [K in Extract<keyof Defs, string> as Defs[K]["signalKey"] extends string
    ? Defs[K]["signalKey"]
    : K]: Defs[K]["kind"] extends "numeric" ? number : string | null;
};

export interface ConditionEngineOptions<Version extends number> {
  version: Version;
  /** Cap on the number of EXCEPT groups. Omit for no cap. */
  maxExceptGroups?: number;
}

export function createConditionEngine<Defs extends Record<string, FieldDefinition>, Version extends number>(
  fieldDefinitions: Defs,
  options: ConditionEngineOptions<Version>,
) {
  type FieldId = Extract<keyof Defs, string>;
  type NumericFieldId = { [K in FieldId]: Defs[K]["kind"] extends "numeric" ? K : never }[FieldId];
  type TagFieldId = { [K in FieldId]: Defs[K]["kind"] extends "tag" ? K : never }[FieldId];
  type CreatorSignals = CreatorSignalsFor<Defs>;

  const fieldIds = Object.keys(fieldDefinitions) as FieldId[];

  const numericValue = z.union([z.number(), z.tuple([z.number(), z.number()])]);
  const comparisonOperators = z.enum(["eq", "gt", "gte", "lt", "lte", "between"]);

  const numericFieldSchemas = fieldIds
    .filter((field) => fieldDefinitions[field].kind === "numeric")
    .map((field) => z.object({ field: z.literal(field), operator: comparisonOperators, value: numericValue }));

  const tagFieldSchemas = fieldIds
    .filter((field) => fieldDefinitions[field].kind === "tag")
    .map((field) => z.object({ field: z.literal(field), operator: z.enum(["is", "is_not"]), value: z.uuid() }));

  const allFieldSchemas = [...numericFieldSchemas, ...tagFieldSchemas] as unknown as [
    (typeof numericFieldSchemas)[number] | (typeof tagFieldSchemas)[number],
    ...((typeof numericFieldSchemas)[number] | (typeof tagFieldSchemas)[number])[],
  ];

  const conditionSchema = z
    .discriminatedUnion("field", allFieldSchemas)
    .refine(
      (c) =>
        fieldDefinitions[c.field as FieldId].kind === "tag"
          ? true
          : c.operator === "between"
            ? Array.isArray(c.value)
            : !Array.isArray(c.value),
      { message: "'between' requires a [min, max] tuple; other operators require a single number" },
    );

  const conditionGroupSchema = z.object({
    logic: z.enum(["all", "any"]),
    conditions: z.array(conditionSchema).min(1),
  });

  const exceptGroupsSchema =
    options.maxExceptGroups !== undefined
      ? z.array(conditionGroupSchema).max(options.maxExceptGroups)
      : z.array(conditionGroupSchema);

  const ruleConditionsSchema = z.object({
    version: z.literal(options.version),
    when: z.array(conditionGroupSchema).min(1),
    except: exceptGroupsSchema,
  });

  type Condition = z.infer<typeof conditionSchema>;
  type ConditionGroup = z.infer<typeof conditionGroupSchema>;
  type RuleConditions = z.infer<typeof ruleConditionsSchema>;

  const SIGNAL_KEY_BY_FIELD = Object.fromEntries(
    fieldIds.map((field) => [field, fieldDefinitions[field].signalKey ?? field]),
  ) as Record<FieldId, keyof CreatorSignals>;

  function evalComparison(actual: number, operator: ComparisonOperator, value: ConditionValue): boolean {
    switch (operator) {
      case "eq":
        return actual === value;
      case "gt":
        return actual > (value as number);
      case "gte":
        return actual >= (value as number);
      case "lt":
        return actual < (value as number);
      case "lte":
        return actual <= (value as number);
      case "between": {
        const [lo, hi] = value as [number, number];
        return actual >= lo && actual <= hi;
      }
    }
  }

  function evalCondition(signals: CreatorSignals, condition: Condition): boolean {
    const field = condition.field as FieldId;
    const signalKey = SIGNAL_KEY_BY_FIELD[field];
    if (fieldDefinitions[field].kind === "tag") {
      const c = condition as { operator: TagOperator; value: string };
      const has = (signals as Record<string, unknown>)[signalKey] === c.value;
      return c.operator === "is" ? has : !has;
    }
    const c = condition as { operator: ComparisonOperator; value: ConditionValue };
    const actual = ((signals as Record<string, unknown>)[signalKey] as number) ?? 0;
    return evalComparison(actual, c.operator, c.value);
  }

  function evalGroup(signals: CreatorSignals, group: ConditionGroup): boolean {
    return group.logic === "all"
      ? group.conditions.every((c) => evalCondition(signals, c))
      : group.conditions.some((c) => evalCondition(signals, c));
  }

  /**
   * A creator matches iff all WHEN groups are true (groups are AND-ed) and, when
   * present, the EXCEPT groups are NOT all true (an exception match suppresses the rule).
   */
  function matchesConditions(signals: CreatorSignals, tree: RuleConditions): boolean {
    const whenMatch = tree.when.every((g) => evalGroup(signals, g));
    if (!whenMatch) return false;
    const exceptMatch = tree.except.length > 0 && tree.except.every((g) => evalGroup(signals, g));
    return !exceptMatch;
  }

  /** Whether any condition, in either the WHEN or EXCEPT groups, references `field`. */
  function referencesField(tree: RuleConditions, field: FieldId): boolean {
    const groups = [...tree.when, ...tree.except];
    return groups.some((g) => g.conditions.some((c) => c.field === field));
  }

  return {
    conditionSchema,
    conditionGroupSchema,
    ruleConditionsSchema,
    matchesConditions,
    referencesField,
    numericFieldIds: fieldIds.filter((field) => fieldDefinitions[field].kind === "numeric") as NumericFieldId[],
    tagFieldIds: fieldIds.filter((field) => fieldDefinitions[field].kind === "tag") as TagFieldId[],
  };
}
